import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Wrench, Globe } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const useNavItems = (t) => [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/about", label: t.nav.about, key: "about" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
];

const LanguageToggle = ({ className = "" }) => {
    const { lang, setLang } = useI18n();
    return (
        <div
            data-testid="lang-toggle"
            className={`inline-flex items-stretch border-2 border-black ${className}`}
        >
            <Globe className="w-4 h-4 self-center mx-2 hidden sm:block" />
            {["en", "fr"].map((code, idx) => (
                <button
                    key={code}
                    type="button"
                    onClick={() => setLang(code)}
                    data-testid={`lang-${code}`}
                    aria-pressed={lang === code}
                    className={`px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                        idx === 0 ? "border-r-2 border-black" : ""
                    } ${
                        lang === code
                            ? "bg-black text-white"
                            : "bg-white text-black hover:bg-zinc-100"
                    }`}
                >
                    {code.toUpperCase()}
                </button>
            ))}
        </div>
    );
};

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const { t } = useI18n();
    const navItems = useNavItems(t);

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 bg-white border-b-2 border-black"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <Link
                        to="/"
                        data-testid="nav-logo"
                        className="flex items-center gap-3 group"
                    >
                        <div className="w-14 h-14 flex items-center justify-center group-hover:bg-[#FF5A00] group-hover:border-[#FF5A00] transition-colors">
                            <img src="/black_icon_transparent_background.png" alt="Logo"/>
                        </div>
                        <div className="leading-none">
                            <div className="font-display text-2xl tracking-wide text-black">
                                PLOMB TECH RÉNO INC.
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF5A00] -mt-0.5">
                                {t.common.tagline}
                            </div>
                        </div>
                    </Link>

                    <nav
                        data-testid="desktop-nav"
                        className="hidden md:flex items-center gap-1"
                    >
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`nav-link-${item.key}`}
                                className={({ isActive }) =>
                                    `px-4 py-2 text-sm font-bold uppercase tracking-[0.18em] transition-colors ${
                                        isActive
                                            ? "text-[#FF5A00]"
                                            : "text-black hover:text-[#FF5A00]"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center gap-3">
                        <LanguageToggle />
                        <a
                            href="tel:+15818889419"
                            data-testid="header-call-btn"
                            className="brutal-btn"
                        >
                            <Phone className="w-4 h-4" /> {t.nav.callNow}<br />{t.common.phoneShort}
                        </a>
                    </div>

                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        className="md:hidden p-2 border-2 border-black"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-nav"
                    className="md:hidden border-t-2 border-black bg-white"
                >
                    <div className="px-4 py-4 flex flex-col gap-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${item.key}`}
                                className={({ isActive }) =>
                                    `px-3 py-3 text-sm font-bold uppercase tracking-[0.18em] border-l-4 ${
                                        isActive
                                            ? "border-[#FF5A00] text-[#FF5A00] bg-zinc-50"
                                            : "border-transparent text-black"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <div className="mt-3 flex items-center justify-between gap-3">
                            <LanguageToggle />
                            <a
                                href="tel:+15818889419"
                                className="brutal-btn flex-1 justify-center"
                                data-testid="mobile-call-btn"
                            >
                                <Phone className="w-4 h-4" /> {t.nav.callNow}<br />{t.common.phoneShort}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export const Footer = () => {
    const { t } = useI18n();
    const navItems = useNavItems(t);
    const year = new Date().getFullYear();
    return (
        <footer
            data-testid="site-footer"
            className="bg-black text-white border-t-2 border-black"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
                    <div className="md:col-span-2">
                        <div className="font-display text-4xl uppercase leading-none">
                            Plomb Tech Réno Inc.<br />
                            <span className="text-[#FF5A00]">Plomberie</span>
                        </div>
                        <p className="mt-5 text-zinc-400 max-w-sm font-medium">
                            {t.common.footerDesc}
                        </p>
                    </div>

                    <div>
                        <div className="overline mb-4">{t.common.footerNavigate}</div>
                        <ul className="space-y-2 font-bold uppercase text-sm tracking-wider">
                            {navItems.map((item) => (
                                <li key={item.to}>
                                    <Link
                                        to={item.to}
                                        className="hover:text-[#FF5A00] transition-colors"
                                        data-testid={`footer-link-${item.key}`}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="overline mb-4">{t.common.footerReach}</div>
                        <ul className="space-y-3 text-sm font-medium text-zinc-300">
                            <li>
                                <a
                                    href="tel:+15818889419"
                                    className="hover:text-[#FF5A00]"
                                    data-testid="footer-phone"
                                >
                                    {t.common.phone}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="mailto:plombtechreno@gmail.com"
                                    className="hover:text-[#FF5A00] break-all"
                                    data-testid="footer-email"
                                >
                                    {t.common.email}
                                </a>
                            </li>
                            <li className="text-zinc-400">
                                {t.common.footerHours}
                                <br />
                                {t.common.footerEmergency}
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-zinc-800 flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
                    <div className="text-xs uppercase tracking-[0.3em] font-bold text-zinc-500">
                        {t.common.footerNote.replace("{year}", year)}
                    </div>
                    <div className="text-xs uppercase tracking-[0.3em] font-bold text-zinc-500">
                        {t.common.footerSlogan}
                    </div>
                </div>
            </div>
        </footer>
    );
};
